import { useState, useEffect, useRef } from "react";
import { api, BACKEND_URL } from "../lib/api";
import { useCustomerAuth } from "../context/CustomerAuthContext";
import { X, Mail, ArrowRight, RotateCcw, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function OtpLoginModal({ onClose, returnPath }) {
  const { refreshUser } = useCustomerAuth();
  const [step,     setStep]     = useState("email");   // "email" | "code"
  const [email,    setEmail]    = useState("");
  const [code,     setCode]     = useState(["", "", "", "", "", ""]);
  const [busy,     setBusy]     = useState(false);
  const [devCode,  setDevCode]  = useState(null);      // shown on screen in dev/no-Resend mode
  const [countdown, setCountdown] = useState(0);
  const codeRefs = useRef([]);

  // Countdown timer for resend
  useEffect(() => {
    if (countdown <= 0) return;
    const t = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [countdown]);

  // Auto-focus first code input when step changes
  useEffect(() => {
    if (step === "code") setTimeout(() => codeRefs.current[0]?.focus(), 100);
  }, [step]);

  const sendCode = async () => {
    if (!email.trim() || !email.includes("@")) { toast.error("Enter a valid email address"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/otp/send", { email: email.trim().toLowerCase() });
      setDevCode(data.dev_code || null);
      setStep("code");
      setCountdown(60);
      toast.success("Code sent — check your email");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send code");
    } finally { setBusy(false); }
  };

  const verifyCode = async () => {
    const fullCode = code.join("");
    if (fullCode.length !== 6) { toast.error("Enter the 6-digit code"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/otp/verify", {
        email: email.trim().toLowerCase(),
        code: fullCode,
      });
      await refreshUser();
      toast.success(`Welcome${data.user?.name ? `, ${data.user.name.split(" ")[0]}` : ""}!`);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Invalid or expired code");
      setCode(["", "", "", "", "", ""]);
      codeRefs.current[0]?.focus();
    } finally { setBusy(false); }
  };

  const handleCodeInput = (idx, val) => {
    val = val.replace(/\D/g, "").slice(-1);
    const next = [...code];
    next[idx] = val;
    setCode(next);
    if (val && idx < 5) codeRefs.current[idx + 1]?.focus();
    if (next.every(d => d !== "")) {
      // Auto-submit when all 6 digits entered
      setTimeout(() => {
        const fullCode = next.join("");
        if (fullCode.length === 6) verifyCodeDirect(fullCode);
      }, 100);
    }
  };

  const verifyCodeDirect = async (fullCode) => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/otp/verify", {
        email: email.trim().toLowerCase(),
        code: fullCode,
      });
      await refreshUser();
      toast.success(`Welcome${data.user?.name ? `, ${data.user.name.split(" ")[0]}` : ""}!`);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Invalid or expired code");
      setCode(["", "", "", "", "", ""]);
      codeRefs.current[0]?.focus();
    } finally { setBusy(false); }
  };

  const handleCodeKeyDown = (idx, e) => {
    if (e.key === "Backspace" && !code[idx] && idx > 0) {
      codeRefs.current[idx - 1]?.focus();
    }
    if (e.key === "Enter") verifyCode();
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (pasted.length === 6) {
      setCode(pasted.split(""));
      codeRefs.current[5]?.focus();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(11,26,59,0.7)", backdropFilter: "blur(4px)" }}
      onClick={onClose}>
      <div
        className="w-full max-w-md rounded-2xl overflow-hidden shadow-2xl"
        style={{ background: "#ffffff", border: "1px solid #e4e8f0" }}
        onClick={e => e.stopPropagation()}>

        {/* ── Header ─────────────────────────────────────────── */}
        <div className="relative px-8 pt-8 pb-6 text-center"
          style={{ background: "linear-gradient(135deg, #0b1a3b 0%, #1e3a6e 100%)" }}>
          <button onClick={onClose}
            className="absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center transition-colors"
            style={{ background: "rgba(255,255,255,0.1)", color: "rgba(255,255,255,0.7)" }}>
            <X className="h-4 w-4" />
          </button>

          {/* BCP Logo */}
          <div className="flex items-center justify-center gap-2 mb-4">
            <span className="font-heading font-black text-[22px] tracking-[0.05em]"
              style={{ color: "#ffffff" }}>BCP</span>
            <span className="font-heading font-black text-[22px]"
              style={{ color: "#e31e24" }}>Car Parts</span>
          </div>

          {step === "email" ? (
            <>
              <div className="font-heading font-bold text-[20px] text-white mb-1">
                Welcome Back
              </div>
              <div className="text-[13px]" style={{ color: "rgba(255,255,255,0.6)" }}>
                Sign in or create an account
              </div>
            </>
          ) : (
            <>
              <div className="font-heading font-bold text-[20px] text-white mb-1">
                Check Your Email
              </div>
              <div className="text-[13px]" style={{ color: "rgba(255,255,255,0.6)" }}>
                We sent a 6-digit code to
              </div>
              <div className="text-[13px] font-semibold mt-0.5" style={{ color: "#60a5fa" }}>
                {email}
              </div>
            </>
          )}
        </div>

        {/* ── Body ───────────────────────────────────────────── */}
        <div className="px-8 py-7 space-y-5">

          {step === "email" ? (
            <>
              {/* ── GOOGLE LOGIN ──────────────────────────────────── */}
              <button
                onClick={() => {
                  localStorage.setItem("bcp_login_return", window.location.pathname);
                  window.location.href = `${BACKEND_URL}/api/auth/google?redirect=${encodeURIComponent(window.location.pathname)}`;
                }}
                className="w-full h-12 rounded-xl flex items-center justify-center gap-3 text-[13px] font-semibold transition-all hover:shadow-md"
                style={{ background: "#ffffff", border: "1px solid #dde3ef", color: "#374151" }}>
                <svg className="h-4 w-4" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continue with Google
              </button>

              {/* Divider */}
              <div className="flex items-center gap-3">
                <div className="flex-1 h-px" style={{ background: "#eef2f8" }} />
                <span className="text-[11px] font-mono uppercase tracking-widest" style={{ color: "#94a3b8" }}>
                  or sign in with email
                </span>
                <div className="flex-1 h-px" style={{ background: "#eef2f8" }} />
              </div>

              {/* Email input */}
              <div className="space-y-2">
                <label className="text-[11px] font-mono uppercase tracking-widest" style={{ color: "#64748b" }}>
                  Email Address
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && sendCode()}
                  placeholder="you@example.com"
                  autoFocus
                  className="w-full h-12 px-4 rounded-xl text-[14px] outline-none transition-all"
                  style={{
                    border: "1.5px solid #dde3ef",
                    background: "#f8fafc",
                    color: "#0f172a",
                  }}
                  onFocus={e => e.target.style.borderColor = "#1e5bb8"}
                  onBlur={e => e.target.style.borderColor = "#dde3ef"}
                />
              </div>

              <button
                onClick={sendCode}
                disabled={busy || !email.trim()}
                className="w-full h-12 rounded-xl font-heading font-bold uppercase tracking-wide text-[14px] text-white flex items-center justify-center gap-2 transition-all"
                style={{
                  background: busy || !email.trim() ? "#94a3b8" : "#e31e24",
                  cursor: busy || !email.trim() ? "not-allowed" : "pointer",
                }}>
                {busy ? "Sending…" : <>Send Code <ArrowRight className="h-4 w-4" /></>}
              </button>

              <p className="text-center text-[11px]" style={{ color: "#94a3b8" }}>
                New to BCP? An account is created automatically on first sign in.
              </p>
            </>
          ) : (
            <>
              {/* Dev code display — only shown when no Resend key */}
              {devCode && (
                <div className="rounded-xl p-4 text-center"
                  style={{ background: "#f0fdf4", border: "1px solid #86efac" }}>
                  <div className="text-[10px] font-mono uppercase tracking-widest mb-1" style={{ color: "#15803d" }}>
                    Dev Mode — Your Code
                  </div>
                  <div className="font-mono font-black text-[32px] tracking-[0.3em]" style={{ color: "#166534" }}>
                    {devCode}
                  </div>
                  <div className="text-[10px] mt-1" style={{ color: "#15803d" }}>
                    This box only appears when email is not configured
                  </div>
                </div>
              )}

              {/* 6-digit code input */}
              <div className="space-y-3">
                <label className="text-[11px] font-mono uppercase tracking-widest" style={{ color: "#64748b" }}>
                  Enter 6-Digit Code
                </label>
                <div className="flex gap-2 justify-between" onPaste={handlePaste}>
                  {code.map((digit, idx) => (
                    <input
                      key={idx}
                      ref={el => codeRefs.current[idx] = el}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={e => handleCodeInput(idx, e.target.value)}
                      onKeyDown={e => handleCodeKeyDown(idx, e)}
                      className="flex-1 h-14 text-center font-mono font-black text-[22px] rounded-xl outline-none transition-all"
                      style={{
                        border: `2px solid ${digit ? "#1e5bb8" : "#dde3ef"}`,
                        background: digit ? "#eff6ff" : "#f8fafc",
                        color: "#0b1a3b",
                        minWidth: 0,
                      }}
                    />
                  ))}
                </div>
              </div>

              <button
                onClick={verifyCode}
                disabled={busy || code.join("").length !== 6}
                className="w-full h-12 rounded-xl font-heading font-bold uppercase tracking-wide text-[14px] text-white flex items-center justify-center gap-2 transition-all"
                style={{
                  background: busy || code.join("").length !== 6 ? "#94a3b8" : "#e31e24",
                  cursor: busy || code.join("").length !== 6 ? "not-allowed" : "pointer",
                }}>
                {busy ? "Verifying…" : <><ShieldCheck className="h-4 w-4" /> Verify & Sign In</>}
              </button>

              {/* Resend + change email */}
              <div className="flex items-center justify-between text-[12px]">
                <button
                  onClick={() => { setStep("email"); setCode(["","","","","",""]); setDevCode(null); }}
                  className="flex items-center gap-1 hover:underline"
                  style={{ color: "#64748b" }}>
                  <X className="h-3 w-3" /> Change email
                </button>
                {countdown > 0 ? (
                  <span style={{ color: "#94a3b8" }}>Resend in {countdown}s</span>
                ) : (
                  <button
                    onClick={() => { setCode(["","","","","",""]); sendCode(); }}
                    className="flex items-center gap-1 hover:underline"
                    style={{ color: "#1e5bb8" }}>
                    <RotateCcw className="h-3 w-3" /> Resend code
                  </button>
                )}
              </div>
            </>
          )}
        </div>

        {/* ── Footer ─────────────────────────────────────────── */}
        <div className="px-8 pb-6 text-center">
          <p className="text-[11px]" style={{ color: "#94a3b8" }}>
            By signing in you agree to our{" "}
            <a href="/terms" className="hover:underline" style={{ color: "#1e5bb8" }}>Terms</a>
            {" & "}
            <a href="/privacy" className="hover:underline" style={{ color: "#1e5bb8" }}>Privacy Policy</a>
          </p>
        </div>
      </div>
    </div>
  );
}